import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { api } from "@/lib/api";

/**
 * Hook réutilisable pour tables triables + visibilité des colonnes.
 *
 * @param {Object} params
 * @param {string} params.storageKey - clé localStorage (unique par table)
 * @param {Array<{key: string, label: string, sortable?: boolean, always?: boolean}>} params.columns
 *   - key: identifiant unique de la colonne
 *   - label: libellé affiché
 *   - sortable: si la colonne peut être triée (default true)
 *   - always: si true, ne peut pas être masquée
 * @param {Object} [params.initialSort] - { key, direction: "asc"|"desc" }
 * @param {boolean} [params.remote=true] - si true, synchronise les préférences sur le backend
 * @returns {{
 *   columns, visibility, toggleVisibility, isVisible,
 *   sortConfig, handleSort, sortArrow, sortItems
 * }}
 */
export function useTableColumns({ storageKey, columns, initialSort = null, remote = true }) {
  const visibilityStorageKey = `${storageKey}:visibility`;
  const sortStorageKey = `${storageKey}:sort`;
  const orderStorageKey = `${storageKey}:order`;

  // --- Column Order (drag-and-drop reordering) ---
  const mergeNewColumns = (storedOrder, defaultOrder) => {
    // Garde uniquement les clés valides et INSÈRE les nouvelles colonnes à la
    // position qu'elles ont dans `defaultOrder` (pas simplement en fin de liste).
    const known = new Set(defaultOrder);
    const cleaned = storedOrder.filter((k) => known.has(k));
    defaultOrder.forEach((k, idx) => {
      if (cleaned.includes(k)) return;
      // Chercher le premier voisin (dans defaultOrder) déjà présent dans cleaned
      let insertAt = cleaned.length; // par défaut, en fin
      for (let j = idx + 1; j < defaultOrder.length; j++) {
        const neighborIdx = cleaned.indexOf(defaultOrder[j]);
        if (neighborIdx !== -1) {
          insertAt = neighborIdx;
          break;
        }
      }
      cleaned.splice(insertAt, 0, k);
    });
    return cleaned;
  };

  const [columnOrder, setColumnOrder] = useState(() => {
    const defaultOrder = columns.map((c) => c.key);
    try {
      const raw = localStorage.getItem(orderStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return mergeNewColumns(parsed, defaultOrder);
        }
      }
    } catch {
      // ignore
    }
    return defaultOrder;
  });

  const persistOrder = useCallback(
    (next) => {
      try {
        localStorage.setItem(orderStorageKey, JSON.stringify(next));
      } catch {
        // ignore
      }
    },
    [orderStorageKey]
  );

  const moveColumn = useCallback(
    (fromKey, toKey) => {
      if (!fromKey || !toKey || fromKey === toKey) return;
      setColumnOrder((prev) => {
        const from = prev.indexOf(fromKey);
        const to = prev.indexOf(toKey);
        if (from < 0 || to < 0) return prev;
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        persistOrder(next);
        return next;
      });
    },
    [persistOrder]
  );

  const resetOrder = useCallback(() => {
    const defaultOrder = columns.map((c) => c.key);
    setColumnOrder(defaultOrder);
    try {
      localStorage.removeItem(orderStorageKey);
    } catch {
      // ignore
    }
  }, [columns, orderStorageKey]);

  // --- Visibility ---
  const [visibility, setVisibility] = useState(() => {
    const defaults = {};
    columns.forEach((c) => {
      defaults[c.key] = true;
    });
    try {
      const raw = localStorage.getItem(visibilityStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...defaults, ...parsed };
      }
    } catch {
      // ignore
    }
    return defaults;
  });

  const persistVisibility = useCallback(
    (next) => {
      try {
        localStorage.setItem(visibilityStorageKey, JSON.stringify(next));
      } catch {
        // ignore
      }
    },
    [visibilityStorageKey]
  );

  const toggleVisibility = useCallback(
    (key) => {
      setVisibility((prev) => {
        const col = columns.find((c) => c.key === key);
        if (col?.always) return prev; // colonne obligatoire
        const next = { ...prev, [key]: !prev[key] };
        persistVisibility(next);
        return next;
      });
    },
    [columns, persistVisibility]
  );

  const isVisible = useCallback((key) => visibility[key] !== false, [visibility]);

  // --- Sorting ---
  const [sortConfig, setSortConfig] = useState(() => {
    try {
      const raw = localStorage.getItem(sortStorageKey);
      if (raw) return JSON.parse(raw);
    } catch {
      // ignore
    }
    return initialSort;
  });

  const handleSort = useCallback(
    (key) => {
      setSortConfig((prev) => {
        let next;
        if (!prev || prev.key !== key) {
          next = { key, direction: "asc" };
        } else if (prev.direction === "asc") {
          next = { key, direction: "desc" };
        } else {
          next = null; // reset
        }
        try {
          if (next) localStorage.setItem(sortStorageKey, JSON.stringify(next));
          else localStorage.removeItem(sortStorageKey);
        } catch {
          // ignore
        }
        return next;
      });
    },
    [sortStorageKey]
  );

  const sortArrow = useCallback(
    (key) => {
      if (!sortConfig || sortConfig.key !== key) return "none";
      return sortConfig.direction;
    },
    [sortConfig]
  );

  /**
   * Trie un tableau de lignes selon le sortConfig courant.
   * @param {Array} items
   * @param {Object<string, (row) => any>} accessors - map key -> fonction d'extraction
   */
  const sortItems = useCallback(
    (items, accessors = {}) => {
      if (!sortConfig) return items;
      const { key, direction } = sortConfig;
      const accessor = accessors[key] || ((row) => row[key]);
      const sorted = [...items].sort((a, b) => {
        const va = accessor(a);
        const vb = accessor(b);
        // Null/undefined/empty string -> toujours à la fin
        const aEmpty = va === null || va === undefined || va === "";
        const bEmpty = vb === null || vb === undefined || vb === "";
        if (aEmpty && bEmpty) return 0;
        if (aEmpty) return 1;
        if (bEmpty) return -1;
        if (typeof va === "number" && typeof vb === "number") {
          return direction === "asc" ? va - vb : vb - va;
        }
        // Essayer date
        const da = va instanceof Date ? va : (typeof va === "string" && /\d{4}-\d{2}-\d{2}/.test(va) ? new Date(va) : null);
        const db = vb instanceof Date ? vb : (typeof vb === "string" && /\d{4}-\d{2}-\d{2}/.test(vb) ? new Date(vb) : null);
        if (da && db && !isNaN(da) && !isNaN(db)) {
          return direction === "asc" ? da - db : db - da;
        }
        const sa = String(va).toLowerCase();
        const sb = String(vb).toLowerCase();
        if (sa < sb) return direction === "asc" ? -1 : 1;
        if (sa > sb) return direction === "asc" ? 1 : -1;
        return 0;
      });
      return sorted;
    },
    [sortConfig]
  );

  const visibleColumns = useMemo(() => {
    const byKey = new Map(columns.map((c) => [c.key, c]));
    const ordered = columnOrder.map((k) => byKey.get(k)).filter(Boolean);
    return ordered.filter((c) => isVisible(c.key));
  }, [columns, columnOrder, isVisible]);

  const orderedColumns = useMemo(() => {
    const byKey = new Map(columns.map((c) => [c.key, c]));
    return columnOrder.map((k) => byKey.get(k)).filter(Boolean);
  }, [columns, columnOrder]);

  // --- Remote sync (optional) ---
  // Au mount : charge les prefs serveur. Elles ont priorité sur localStorage
  // (le serveur est la source de vérité multi-device).
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!remote) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.getUserTablePref(storageKey);
        if (cancelled) return;
        if (data.visibility && Object.keys(data.visibility).length > 0) {
          const defaults = {};
          columns.forEach((c) => { defaults[c.key] = true; });
          setVisibility({ ...defaults, ...data.visibility });
        }
        if (Array.isArray(data.order) && data.order.length > 0) {
          const defaultOrder = columns.map((c) => c.key);
          setColumnOrder(mergeNewColumns(data.order, defaultOrder));
        }
        if (data.sort !== undefined) setSortConfig(data.sort || null);
      } catch {
        // Silencieux : on reste sur localStorage si l'endpoint échoue.
      } finally {
        hydratedRef.current = true;
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, remote]);

  // À chaque change (après hydration), push les prefs serveur avec un debounce.
  const syncTimerRef = useRef(null);
  useEffect(() => {
    if (!remote) return;
    if (!hydratedRef.current) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      api.upsertUserTablePref(storageKey, {
        visibility,
        order: columnOrder,
        sort: sortConfig,
      }).catch(() => { /* ignore */ });
    }, 600);
    return () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    };
  }, [remote, storageKey, visibility, columnOrder, sortConfig]);

  return {
    columns,
    orderedColumns,
    visibleColumns,
    visibility,
    toggleVisibility,
    isVisible,
    sortConfig,
    handleSort,
    sortArrow,
    sortItems,
    columnOrder,
    moveColumn,
    resetOrder,
  };
}
